import { useCallback, useMemo, useRef, useState } from 'react'
import {
  DimmingProfileDraft,
  LEVEL_INACTIVE,
  MAX_LEVEL,
  MAX_TIME_MINUTES,
  ProfileStep,
} from './types/dimmingProfile.types'
import {
  DimmingProfileEditorActions,
  DimmingProfileEditorState,
  EditorGeometry,
} from './types/presenter.types'
import { buildDaliAddress, parseDaliAddress } from './utils/daliAddress'
import {
  clampLevel,
  hasTimeCollision,
  snapTimeMinutes,
  sortSteps,
} from './utils/stepMath'

// Fixed plot geometry (the SVG scales via viewBox in the view).
const GEOMETRY: EditorGeometry = {
  width: 720,
  height: 320,
  marginLeft: 44,
  marginRight: 16,
  marginTop: 16,
  marginBottom: 36,
  plotWidth: 720 - 44 - 16,
  plotHeight: 320 - 16 - 36,
}

interface UseDimmingProfileEditorParams {
  value: DimmingProfileDraft
  onChange: (next: DimmingProfileDraft) => void
  svgRef: React.RefObject<SVGSVGElement>
}

/**
 * Presenter hook for the DimmingProfileEditor. The editor is a controlled
 * component: `value` is the current draft, `onChange` emits the next draft on
 * every edit. All x-positions snap to the 10-minute grid (plan §3).
 */
export const useDimmingProfileEditorPresenter = ({
  value,
  onChange,
  svgRef,
}: UseDimmingProfileEditorParams): {
  state: DimmingProfileEditorState
  actions: DimmingProfileEditorActions
} => {
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null)
  const [draggingStepIndex, setDraggingStepIndex] = useState<number | null>(null)
  // Identité du step dragué : son temps (unique, snappé), PAS son index — sinon
  // périmé après le re-tri de `emit` (cf. CalendarStepsPresenter, revue MR !2).
  const draggingTimeRef = useRef<number | null>(null)
  // Remembers the last numeric level per step (keyed by step time, which is
  // unique) so toggling "Inactif" OFF restores the prior value instead of 0.
  const lastLevelRef = useRef<Record<number, number>>({})

  const steps = useMemo(() => sortSteps(value.steps), [value.steps])
  const daliValue = useMemo(
    () => parseDaliAddress(value.dali_address),
    [value.dali_address],
  )

  // ── coordinate conversions ────────────────────────────────────────────────
  const timeToX = useCallback((timeMinutes: number): number => {
    const ratio = timeMinutes / MAX_TIME_MINUTES
    return GEOMETRY.marginLeft + ratio * GEOMETRY.plotWidth
  }, [])

  const levelToY = useCallback((level: number): number => {
    // Inactive renders at the baseline (0%) so it's visible but distinct.
    const effective = level === LEVEL_INACTIVE ? 0 : level
    const ratio = effective / MAX_LEVEL
    return GEOMETRY.marginTop + (1 - ratio) * GEOMETRY.plotHeight
  }, [])

  // Convert a pointer position (client coords) into snapped (time, level).
  const clientToData = useCallback(
    (clientX: number, clientY: number): { time: number; level: number } => {
      const svg = svgRef.current
      if (!svg) return { time: 0, level: 0 }
      const rect = svg.getBoundingClientRect()
      // Map screen px -> viewBox units (viewBox is 0..GEOMETRY.width).
      const vbX = ((clientX - rect.left) / rect.width) * GEOMETRY.width
      const vbY = ((clientY - rect.top) / rect.height) * GEOMETRY.height
      const ratioX =
        (vbX - GEOMETRY.marginLeft) / GEOMETRY.plotWidth
      const ratioY =
        (vbY - GEOMETRY.marginTop) / GEOMETRY.plotHeight
      const time = snapTimeMinutes(ratioX * MAX_TIME_MINUTES)
      const level = clampLevel((1 - ratioY) * MAX_LEVEL)
      return { time, level }
    },
    [svgRef],
  )

  // ── emit helpers ──────────────────────────────────────────────────────────
  const emit = useCallback(
    (nextSteps: ProfileStep[], patch?: Partial<DimmingProfileDraft>) => {
      onChange({
        steps: sortSteps(nextSteps),
        active_days: patch?.active_days ?? value.active_days,
        dali_address: patch?.dali_address ?? value.dali_address,
      })
    },
    [onChange, value.active_days, value.dali_address],
  )

  // ── step actions ──────────────────────────────────────────────────────────
  const addStepAt = useCallback(
    (timeMinutes: number, level: number) => {
      const t = snapTimeMinutes(timeMinutes)
      if (hasTimeCollision(steps, t)) return
      emit([...steps, { step_time_minutes: t, dimming_level: clampLevel(level) }])
    },
    [steps, emit],
  )

  const removeStep = useCallback(
    (index: number) => {
      const next = steps.filter((_, i) => i !== index)
      emit(next)
      setSelectedStepIndex(null)
    },
    [steps, emit],
  )

  const setStepLevel = useCallback(
    (index: number, level: number) => {
      const clamped = clampLevel(level)
      const t = steps[index]?.step_time_minutes
      if (t !== undefined) lastLevelRef.current[t] = clamped
      const next = steps.map((s, i) =>
        i === index ? { ...s, dimming_level: clamped } : s,
      )
      emit(next)
    },
    [steps, emit],
  )

  const setStepTime = useCallback(
    (index: number, timeMinutes: number) => {
      const t = snapTimeMinutes(timeMinutes)
      if (hasTimeCollision(steps, t, index)) return
      // Keep the remembered-level memory attached to the step as its time moves.
      const oldT = steps[index]?.step_time_minutes
      if (
        oldT !== undefined &&
        oldT !== t &&
        lastLevelRef.current[oldT] !== undefined
      ) {
        lastLevelRef.current[t] = lastLevelRef.current[oldT]
        delete lastLevelRef.current[oldT]
      }
      const next = steps.map((s, i) =>
        i === index ? { ...s, step_time_minutes: t } : s,
      )
      emit(next)
    },
    [steps, emit],
  )

  const toggleStepInactive = useCallback(
    (index: number) => {
      const next = steps.map((s, i) => {
        if (i !== index) return s
        if (s.dimming_level === LEVEL_INACTIVE) {
          // Un-checking "Inactif": restore the remembered numeric level
          // (default to full brightness if none was recorded).
          const restored = lastLevelRef.current[s.step_time_minutes] ?? MAX_LEVEL
          return { ...s, dimming_level: clampLevel(restored) }
        }
        // Checking "Inactif": remember the current numeric level first.
        lastLevelRef.current[s.step_time_minutes] = s.dimming_level
        return { ...s, dimming_level: LEVEL_INACTIVE }
      })
      emit(next)
    },
    [steps, emit],
  )

  // ── drag lifecycle ────────────────────────────────────────────────────────
  const beginDrag = useCallback(
    (index: number) => {
      draggingTimeRef.current = steps[index]?.step_time_minutes ?? null
      setDraggingStepIndex(index)
      setSelectedStepIndex(index)
    },
    [steps],
  )

  const dragTo = useCallback(
    (clientX: number, clientY: number) => {
      const trackedTime = draggingTimeRef.current
      if (trackedTime === null) return
      // Re-résout l'index depuis l'identité temps (l'array a pu être re-trié).
      const index = steps.findIndex((s) => s.step_time_minutes === trackedTime)
      if (index < 0) return
      const { time, level } = clientToData(clientX, clientY)
      // Preserve inactive state while dragging vertically.
      const wasInactive = steps[index]?.dimming_level === LEVEL_INACTIVE
      const nextLevel = wasInactive ? LEVEL_INACTIVE : level
      // Reject horizontal move that collides with another step's slot.
      const t = hasTimeCollision(steps, time, index)
        ? steps[index].step_time_minutes
        : time
      const next = steps.map((s, i) =>
        i === index
          ? { ...s, step_time_minutes: t, dimming_level: nextLevel }
          : s,
      )
      // Suit le step dragué par son nouveau temps à travers le re-tri.
      draggingTimeRef.current = t
      setDraggingStepIndex(
        sortSteps(next).findIndex((s) => s.step_time_minutes === t),
      )
      emit(next)
    },
    [steps, clientToData, emit],
  )

  const endDrag = useCallback(() => {
    draggingTimeRef.current = null
    setDraggingStepIndex(null)
  }, [])

  const handlePlotClick = useCallback(
    (clientX: number, clientY: number) => {
      const { time, level } = clientToData(clientX, clientY)
      addStepAt(time, level)
    },
    [clientToData, addStepAt],
  )

  // ── days ──────────────────────────────────────────────────────────────────
  const toggleDay = useCallback(
    (bit: number) => {
      const nextDays = value.active_days ^ bit
      emit(steps, { active_days: nextDays & 0xff })
    },
    [value.active_days, steps, emit],
  )

  // ── dali address ──────────────────────────────────────────────────────────
  const setDaliMode = useCallback(
    (mode: ReturnType<typeof parseDaliAddress>['mode']) => {
      const nextAddr = buildDaliAddress({ mode, id: daliValue.id })
      emit(steps, { dali_address: nextAddr & 0xff })
    },
    [daliValue.id, steps, emit],
  )

  const setDaliId = useCallback(
    (id: number) => {
      const nextAddr = buildDaliAddress({ mode: daliValue.mode, id })
      emit(steps, { dali_address: nextAddr & 0xff })
    },
    [daliValue.mode, steps, emit],
  )

  const selectStep = useCallback((index: number | null) => {
    setSelectedStepIndex(index)
  }, [])

  const state: DimmingProfileEditorState = {
    steps,
    activeDays: value.active_days,
    daliAddress: value.dali_address,
    daliValue,
    selectedStepIndex,
    draggingStepIndex,
    geometry: GEOMETRY,
  }

  const actions: DimmingProfileEditorActions = {
    addStepAt,
    removeStep,
    selectStep,
    setStepLevel,
    setStepTime,
    toggleStepInactive,
    beginDrag,
    dragTo,
    endDrag,
    handlePlotClick,
    toggleDay,
    setDaliMode,
    setDaliId,
    timeToX,
    levelToY,
  }

  return { state, actions }
}
